// ユーザーアバター画像。
// IdP がアイコン URL を返さない場合や、画像取得に失敗した場合でも
// 必ずフォールバック（イニシャル / アイコン）を表示できるようにする。

import { AccountCircleIconData } from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useEffect, useMemo, useState } from "react";
import { isRenderableAvatarImageUrl } from "../../auth/avatar-image-url";

interface Props {
  readonly src?: string | undefined;
  readonly displayName: string;
  readonly alt: string;
  readonly className?: string | undefined;
  /**
   * https アバターを許可する origin の allowlist。リモート参加者（他者）由来のアバターを
   * 表示する場合に信頼済み origin（IdP）を渡す。未指定（自分自身のアバター）は任意 https を許容。
   */
  readonly allowedHttpsOrigins?: readonly string[] | undefined;
}

export function UserAvatar({
  src,
  displayName,
  alt,
  className,
  allowedHttpsOrigins,
}: Props): JSX.Element {
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const [errored, setErrored] = useState(false);

  // src が変わったら一度エラー状態をリセットする。
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const safeSrc = sanitizeImageSrc(src);
  // data:image/... か（allowlist 指定時は許可 origin の）https の URL のみ <img src> に流す。
  const showImage =
    safeSrc !== undefined &&
    isRenderableAvatarImageUrl(safeSrc, { allowedHttpsOrigins }) &&
    !errored;

  if (showImage) {
    return (
      <img
        alt={alt}
        className={`dashboard-user-avatar ${className ?? ""}`.trim()}
        onError={() => setErrored(true)}
        referrerPolicy="no-referrer"
        src={safeSrc}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`dashboard-user-avatar dashboard-user-avatar-fallback ${className ?? ""}`.trim()}
    >
      {initials.length > 0 ? initials : <Icon d={AccountCircleIconData} />}
    </span>
  );
}

// 空文字列・"null" などを undefined に正規化する。
function sanitizeImageSrc(src: string | null | undefined): string | undefined {
  if (src == null) return undefined;
  const trimmed = src.trim();
  if (trimmed.length === 0) return undefined;
  if (trimmed === "null" || trimmed === "undefined") return undefined;
  return trimmed;
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/u).filter((part) => part.length > 0);
  if (parts.length === 0) return "";
  if (parts.length === 1) {
    // 単一トークンの場合は先頭 1 文字（CJK でも英字でも 1 文字を表示）。
    const first = parts[0]!;
    return takeFirstGrapheme(first).toUpperCase();
  }
  // 複数トークンなら先頭 2 個の頭文字を結合。
  return parts
    .slice(0, 2)
    .map((part) => takeFirstGrapheme(part))
    .join("")
    .toUpperCase();
}

// サロゲートペアや結合文字を考慮して先頭の 1 グラフェム（または 1 文字）を取得する。
function takeFirstGrapheme(value: string): string {
  if (value.length === 0) return "";
  // ES2018 以降の Intl.Segmenter があれば使用。
  const SegmenterCtor = (Intl as unknown as { Segmenter?: typeof Intl.Segmenter | undefined }).Segmenter;
  if (typeof SegmenterCtor === "function") {
    const segmenter = new SegmenterCtor("ja", { granularity: "grapheme" });
    const first = segmenter.segment(value)[Symbol.iterator]().next().value as
      | { segment: string }
      | undefined;
    if (first) return first.segment;
  }
  // フォールバックとしてコードポイント単位の先頭 1 つ。
  return Array.from(value)[0] ?? "";
}
